/**
 * Phase 276 — Shared Record Payment dialog.
 *
 * Used by both:
 *   • ApartmentDetails.js  (apartmentId fixed, no selector)
 *   • PaymentsTab.js       (no apartmentId, shows apartment dropdown)
 *
 * Why one component:
 *   - Identical fields, layout, allocation logic, validation rules, and
 *     receipt-number behaviour everywhere.
 *   - Single POST endpoint (`/receipts`) → no duplicate write paths.
 *   - Edits on either screen are reflected on the other via fetchAll callbacks.
 *
 * Props:
 *   open, onOpenChange         — dialog open state.
 *   buildingId   (required)    — used for the POST payload.
 *   apartmentId  (optional)    — when provided, apartment is locked.
 *   apartments   (required when no apartmentId) — for the selector.
 *   apartmentNumber (optional) — pretty title suffix when locked.
 *   onSaved (optional)         — called after a successful save.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CheckCircle, Receipt } from '@phosphor-icons/react';
import { useLanguage } from '../context/LanguageContext';
import DatePickerDMY from './DatePickerDMY';
import { OwnerSelect, defaultOwnerId } from './OwnerSelect';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Phase 296 — Simple DD/MM/YYYY formatter for the Unpaid Invoices table.
const fmtDMY = (d) => {
  if (!d) return '';
  const p = String(d).slice(0, 10).split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d;
};
const round2 = (n) => Math.round(n * 100) / 100;

export default function RecordPaymentDialog({
  open,
  onOpenChange,
  buildingId,
  apartmentId,        // when set, locks apartment
  apartments,         // required when apartmentId not set
  apartmentNumber,    // optional title hint
  onSaved,
}) {
  const { t } = useLanguage();
  const lockedApt = Boolean(apartmentId);

  const [form, setForm] = useState({
    apartment_id: apartmentId || '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'cash',
    receipt_number: '',
    notes: '',
    total_amount: '',
    // Phase 292 — selected owner_history_id. Empty string ("") means "All
    // owners" / no filter. When a specific owner is chosen, only that
    // owner-period's unpaid invoices are eligible and the receipt is stamped
    // with the owner_history_id so the audit trail is permanent.
    owner_history_id: '',
  });
  const [invoices, setInvoices] = useState([]);
  const [selected, setSelected] = useState({});
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [saving, setSaving] = useState(false);
  // Phase 292 — Owner history list for the selected apartment. Populated
  // whenever apartment changes. The Owner selector is rendered only when
  // 2+ rows exist (i.e. there's actually a previous owner to choose from).
  const [ownerHistory, setOwnerHistory] = useState([]);

  // Reset when dialog opens / closes
  useEffect(() => {
    if (!open) return;
    setForm({
      apartment_id: apartmentId || '',
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'cash',
      receipt_number: '',
      notes: '',
      total_amount: '',
      owner_history_id: '',
    });
    setSelected({});
    setInvoices([]);
    setOwnerHistory([]);
  }, [open, apartmentId]);

  // Phase 292 — Fetch owner history when apartment changes so the Owner
  // dropdown can offer the right choices and the invoice list can be
  // filtered by owner period.
  useEffect(() => {
    let cancelled = false;
    if (!open || !form.apartment_id) {
      setOwnerHistory([]);
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/apartments/${form.apartment_id}/owner-history`);
        if (!cancelled) {
          const rows = res.data || [];
          setOwnerHistory(rows);
          // One active owner → preselected (a previous owner can still be chosen).
          if (rows.length >= 2) setForm((f) => ({ ...f, owner_history_id: f.owner_history_id || defaultOwnerId(rows) }));
        }
      } catch {
        if (!cancelled) setOwnerHistory([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, form.apartment_id]);

  // Fetch unpaid invoices whenever the chosen apartment changes.
  useEffect(() => {
    let cancelled = false;
    if (!open || !form.apartment_id) {
      setInvoices([]);
      return;
    }
    (async () => {
      try {
        setLoadingInvoices(true);
        const res = await axios.get(`${API}/apartments/${form.apartment_id}/payable-items`);
        if (cancelled) return;
        const items = (res.data || []).filter(it => (it.remaining || 0) > 0.01);
        setInvoices(items);
        const sel = {};
        items.forEach(it => { sel[it.invoice_id] = { amount: 0 }; });
        setSelected(sel);
      } catch {
        if (!cancelled) setInvoices([]);
      } finally {
        if (!cancelled) setLoadingInvoices(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, form.apartment_id]);

  // Phase 292 — Filter unpaid invoices to those that fall inside the
  // selected owner's [start_date, end_date] window. Empty selection means
  // no filter (show every unpaid invoice).
  const filteredInvoices = useMemo(() => {
    if (!form.owner_history_id) return invoices;
    const owner = ownerHistory.find(o => o.owner_history_id === form.owner_history_id);
    if (!owner) return invoices;
    const today = new Date().toISOString().split('T')[0];
    const startD = owner.start_date || '0000-00-00';
    const endD = owner.end_date || today;
    return invoices.filter(inv => {
      // Opening balance virtual rows attach to the earliest owner record.
      if (inv.is_opening_balance) {
        const earliest = [...ownerHistory].sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''))[0];
        return earliest && earliest.owner_history_id === form.owner_history_id;
      }
      const d = (inv.invoice_date || inv.period_start || inv.period || '').slice(0, 10);
      if (!d) return true; // unable to date → include defensively
      return d >= startD && d <= endD;
    });
  }, [invoices, ownerHistory, form.owner_history_id]);

  const totalAllocated = useMemo(
    () => Object.values(selected).reduce((s, x) => s + (parseFloat(x?.amount) || 0), 0),
    [selected]
  );
  const totalAmount = parseFloat(form.total_amount) || 0;
  const overpayment = totalAmount > 0 ? Math.max(0, round2(totalAmount - totalAllocated)) : 0;
  const overAllocated = totalAllocated > totalAmount + 0.01;
  const allocationValid = totalAmount > 0 && !overAllocated;

  const autoDistribute = useCallback(() => {
    if (totalAmount <= 0) return;
    let pool = totalAmount;
    const sorted = [...filteredInvoices].sort((a, b) => (a.period || '').localeCompare(b.period || ''));
    const next = {};
    sorted.forEach(inv => {
      const remaining = inv.remaining || 0;
      const apply = pool > 0 && remaining > 0 ? Math.min(pool, remaining) : 0;
      next[inv.invoice_id] = { amount: Math.round(apply * 100) / 100 };
      pool = Math.round((pool - apply) * 100) / 100;
    });
    setSelected(next);
  }, [totalAmount, filteredInvoices]);

  const updateAllocation = (invoiceId, value) => {
    const inv = filteredInvoices.find(i => i.invoice_id === invoiceId);
    const maxVal = inv ? (inv.remaining || 0) : 99999;
    const num = Math.min(parseFloat(value) || 0, maxVal);
    setSelected(prev => ({ ...prev, [invoiceId]: { amount: Math.max(0, num) } }));
  };

  // Full outstanding of the (owner-filtered) unpaid items; each allocation capped at its remaining → no over/duplicate allocation.
  const outstandingTotal = useMemo(() => round2(filteredInvoices.reduce((s, i) => s + Math.max(0, Number(i.remaining || 0)), 0)), [filteredInvoices]);
  const payFullOutstanding = () => {
    const next = {};
    invoices.forEach((i) => { next[i.invoice_id] = { amount: 0 }; });
    filteredInvoices.forEach((i) => { next[i.invoice_id] = { amount: round2(Math.max(0, Number(i.remaining || 0))) }; });
    setSelected(next);
    setForm((f) => ({ ...f, total_amount: outstandingTotal.toFixed(2) }));
  };
  const payInvoiceInFull = (inv) => {
    const rem = round2(Math.max(0, Number(inv.remaining || 0)));
    setSelected((prev) => {
      const next = { ...prev, [inv.invoice_id]: { amount: rem } };
      const sum = round2(Object.values(next).reduce((s, x) => s + (parseFloat(x?.amount) || 0), 0));
      setForm((f) => ({ ...f, total_amount: Math.max(parseFloat(f.total_amount) || 0, sum).toFixed(2) }));
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!form.apartment_id) { toast.error(t('selectApartment') || 'Select apartment'); return; }
    if (totalAmount <= 0) { toast.error(t('amountRequired') || 'Enter amount'); return; }
    if (overAllocated) { toast.error(t('allocationMismatch') || 'Allocation exceeds amount'); return; }

    const invoice_selections = Object.entries(selected)
      .filter(([, s]) => (parseFloat(s?.amount) || 0) > 0)
      .map(([id, s]) => ({ invoice_id: id, amount: parseFloat(s.amount) || 0 }));

    try {
      setSaving(true);
      await axios.post(`${API}/receipts`, {
        receipt_type: 'common_expense',
        building_id: buildingId,
        apartment_id: form.apartment_id,
        payment_date: form.payment_date,
        payment_method: form.payment_method,
        notes: form.notes,
        amount: totalAmount,
        invoice_selections,
        receipt_number: (form.receipt_number || '').trim(),
        // Phase 292 — Forward the selected owner so the payment + receipt
        // stay linked to that historical owner forever.
        ...(form.owner_history_id ? { owner_history_id: form.owner_history_id } : {}),
      });
      toast.success(t('savedSuccess') || t('paymentRecorded'));
      if (overpayment > 0.01) {
        toast.info(`${t('overpaymentRecorded') || t('overpaymentCredit') || 'Saved as credit'}: €${overpayment.toFixed(2)}`);
      }
      // Phase 1010 — Broadcast a global refresh signal so any other
      // component currently showing this apartment (e.g. the Apartments
      // list panel inside BuildingDetails) can re-fetch its data
      // WITHOUT requiring the user to navigate away and back. Payload
      // includes building_id + apartment_id so listeners can decide
      // whether the event is relevant.
      try {
        window.dispatchEvent(new CustomEvent('cleanpro:apartment-payment-saved', {
          detail: {
            building_id: buildingId,
            apartment_id: apartmentId,
            amount: totalAmount,
            saved_at: new Date().toISOString(),
          },
        }));
      } catch { /* non-fatal */ }
      onOpenChange?.(false);
      onSaved?.();
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(t('receiptNumberAlreadyExists') || 'Receipt number already exists.');
      } else {
        toast.error(err.response?.data?.detail || t('errorOccurred') || 'Error');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-6xl max-h-[92vh] overflow-y-auto rounded-none" data-testid="record-payment-dialog">
        <DialogHeader className="border-b border-zinc-200 pb-4">
          <DialogTitle className="text-2xl font-light tracking-tight flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
            <Receipt size={26} weight="duotone" className="text-emerald-600" />
            {t('recordPayment')}
            {lockedApt && apartmentNumber && <span className="text-zinc-400 font-light">— #{apartmentNumber}</span>}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 pt-2" data-testid="record-payment-form">
          {/* Apartment selector — alone on its own row when the dialog is opened
              from a non-locked context (Building Payments). When lockedApt, the
              apartment number is shown in the header, so we hide this row. */}
          {!lockedApt && (
            <div>
              <Label className="text-sm font-medium text-zinc-700">{t('apartment')} *</Label>
              <Select value={form.apartment_id} onValueChange={v => setForm({ ...form, apartment_id: v })}>
                <SelectTrigger className="mt-1 rounded-none" data-testid="payment-apartment-select"><SelectValue placeholder={t('selectApartment') || 'Select apartment'} /></SelectTrigger>
                <SelectContent className="rounded-none max-h-[300px]">
                  {(apartments || []).map(a => (
                    <SelectItem key={a.apartment_id} value={a.apartment_id}>
                      #{a.apartment_number} — {a.owner_name || a.tenant_name || ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Phase 296 — Top fields reordered:
                Row 1: Total Payment + Date  (most important, first visible)
                Row 2: Payment Method + Receipt # */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-zinc-700">{t('totalPayment') || t('amount')} (&euro;) *</Label>
              <div className="flex gap-1 mt-1">
              <Input
                type="number" step="0.01" min="0.01"
                value={form.total_amount}
                onChange={e => setForm({ ...form, total_amount: e.target.value })}
                placeholder="0.00"
                required
                className="rounded-none font-mono text-lg font-bold"
                data-testid="payment-amount-input"
              />
              <Button type="button" variant="outline" onClick={payFullOutstanding} disabled={outstandingTotal <= 0}
                className="rounded-none h-10 text-xs whitespace-nowrap border-emerald-300 text-emerald-800 hover:bg-emerald-50" data-testid="pay-full-amount-btn"
                title={`€${outstandingTotal.toFixed(2)}`}>
                {t('payFullAmount') || 'Pay full amount'}
              </Button>
              </div>
              {outstandingTotal > 0 && <p className="text-[11px] text-zinc-500 mt-1" data-testid="pay-full-amount-hint">{t('outstandingBalance') || 'Outstanding'}: €{outstandingTotal.toFixed(2)}</p>}
            </div>
            <div>
              <Label className="text-sm font-medium text-zinc-700">{t('date')}</Label>
              <DatePickerDMY
                value={form.payment_date}
                onChange={v => setForm({ ...form, payment_date: v })}
                className="mt-1 font-mono"
                required
                data-testid="payment-date-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-zinc-700">{t('paymentMethod') || t('source')}</Label>
              <Select value={form.payment_method} onValueChange={v => setForm({ ...form, payment_method: v })}>
                <SelectTrigger className="mt-1 rounded-none" data-testid="payment-source-select"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="cash">{t('cash')}</SelectItem>
                  <SelectItem value="bank">{t('bank')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium text-zinc-700">
                {t('receiptNumber')} <span className="text-zinc-400 text-xs font-normal">({t('optional') || 'optional'})</span>
              </Label>
              <Input
                value={form.receipt_number}
                onChange={e => setForm({ ...form, receipt_number: e.target.value })}
                placeholder={t('receiptAutoHint') || 'Leave empty to auto-generate (REC-YYYY-NNNN)'}
                maxLength={64}
                className="mt-1 rounded-none font-mono"
                autoComplete="off"
                data-testid="payment-receipt-input"
              />
            </div>
          </div>

          {/* Phase 292 — Owner selector (only when there's history of 2+ owners,
              which means there's actually a previous owner to choose from). */}
          {form.apartment_id && ownerHistory.length >= 2 && (
            <OwnerSelect owners={ownerHistory} value={form.owner_history_id}
              onChange={(v) => setForm((f) => ({ ...f, owner_history_id: v }))}
              hint={t('selectOwnerHint') || 'Filters unpaid invoices to the selected owner. The payment will stay permanently linked to this owner.'} />
          )}

          {/* Auto-distribute button (only when there are invoices and a total) */}
          {totalAmount > 0 && filteredInvoices.length > 0 && (
            <div className="flex justify-end">
              <Button type="button" variant="outline" size="sm" onClick={autoDistribute} className="rounded-none text-xs" data-testid="auto-distribute-btn">
                {t('autoDistribute') || 'Auto-distribute (FIFO)'}
              </Button>
            </div>
          )}

          {/* Invoice allocation table */}
          {form.apartment_id && (
            <div data-testid="unpaid-invoices-panel">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium text-zinc-700">{t('unpaidInvoices') || 'Unpaid Invoices'}</Label>
                <span className="text-xs font-mono text-zinc-500" data-testid="unpaid-invoices-total">
                  {t('totalDue') || 'Total Due'}: <span className="font-semibold text-red-600">€{filteredInvoices.reduce((s, i) => s + (i.remaining || 0), 0).toFixed(2)}</span>
                </span>
              </div>
              <div className="border border-zinc-200 max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead><tr className="bg-zinc-50 border-b border-zinc-200 sticky top-0 z-10">
                    {/* Phase 296 — Column order: Date | Period | Reference | Description | Remaining | Allocate */}
                    <th className="px-3 py-2 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">{t('date') || 'Date'}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">{t('period') || 'Period'}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">{t('reference') || 'Reference'}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">{t('description') || 'Description'}</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-zinc-500 uppercase tracking-wider">{t('remaining') || 'Remaining'}</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-zinc-500 uppercase tracking-wider">{t('allocate') || 'Allocate'}</th>
                  </tr></thead>
                  <tbody className="divide-y divide-zinc-100">
                    {loadingInvoices && (
                      <tr><td colSpan={6} className="text-center py-6 text-zinc-400">{t('loading') || 'Loading…'}</td></tr>
                    )}
                    {!loadingInvoices && filteredInvoices.length === 0 && (
                      <tr><td colSpan={6} className="text-center py-6 text-zinc-400" data-testid="no-unpaid-invoices">
                        {form.owner_history_id
                          ? (t('noUnpaidInvoicesForOwner') || 'No unpaid invoices for the selected owner.')
                          : (t('noUnpaidInvoicesCreditOnly') || t('noUnpaidInvoices') || 'No unpaid invoices — full amount will be saved as credit.')}
                      </td></tr>
                    )}
                    {!loadingInvoices && [...filteredInvoices]
                      // Phase 296 — Sort by actual invoice_date (oldest first
                      // for consistent FIFO-friendly display). Opening Balance
                      // always pinned at the very top.
                      .sort((a, b) => {
                        if (a.is_opening_balance && !b.is_opening_balance) return -1;
                        if (!a.is_opening_balance && b.is_opening_balance) return 1;
                        const da = (a.invoice_date || a.created_at || '').slice(0, 10) || '9999-12-31';
                        const db = (b.invoice_date || b.created_at || '').slice(0, 10) || '9999-12-31';
                        return da.localeCompare(db);
                      })
                      .map(inv => {
                      const alloc = selected[inv.invoice_id]?.amount || 0;
                      const isOver = alloc > (inv.remaining || 0) + 0.01;
                      const isOpening = !!inv.is_opening_balance;
                      const obDesc = isOpening
                        ? (inv.opening_balance_notes ? `${t('openingBalance') || 'Opening Balance'} – ${inv.opening_balance_notes}` : (t('openingBalance') || 'Opening Balance'))
                        : (t('invoice') || 'Invoice');
                      const dateStr = isOpening
                        ? (inv.opening_balance_date ? fmtDMY(inv.opening_balance_date) : '—')
                        : fmtDMY(inv.invoice_date);
                      return (
                        <tr key={inv.invoice_id} data-testid={`inv-row-${inv.invoice_id}`}>
                          <td className="px-3 py-2 font-mono text-zinc-700 whitespace-nowrap">{dateStr || '—'}</td>
                          <td className="px-3 py-2 font-mono text-zinc-700">
                            {isOpening ? <span className="text-[10px] uppercase tracking-wider text-zinc-500">OPENING</span> : (inv.period || '—')}
                          </td>
                          <td className="px-3 py-2 font-mono text-xs text-zinc-700">
                            {isOpening ? '—' : (inv.invoice_number || '')}
                          </td>
                          <td className="px-3 py-2 text-xs text-zinc-700">
                            <span title={obDesc}>{obDesc}</span>
                          </td>
                          <td className="px-3 py-2 text-right font-mono font-semibold text-red-600">&euro;{(inv.remaining || 0).toFixed(2)}</td>
                          <td className="px-3 py-2 text-right">
                            <div className="flex items-center justify-end gap-1">
                            <button type="button" disabled={Number(inv.remaining || 0) <= 0} onClick={() => payInvoiceInFull(inv)}
                              className="text-[10px] uppercase tracking-wider px-1.5 h-8 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"
                              title={t('payThisInvoiceInFull') || 'Pay this invoice in full'} data-testid={`pay-full-inv-${inv.invoice_id}`}>{t('fullShort') || 'Full'}</button>
                            <Input
                              type="number" step="0.01" min="0"
                              max={Math.max(0, Number(inv.remaining || 0))}
                              disabled={Number(inv.remaining || 0) <= 0}
                              value={alloc || ''}
                              onChange={e => updateAllocation(inv.invoice_id, e.target.value)}
                              placeholder="0"
                              className={`w-28 rounded-none font-mono text-sm h-8 text-right disabled:bg-zinc-100 disabled:cursor-not-allowed ${isOver ? 'border-red-400 bg-red-50' : ''}`}
                              data-testid={`alloc-${inv.invoice_id}`}
                            />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Allocation summary */}
          {totalAmount > 0 && (
            <div
              className={`flex flex-col gap-2 px-4 py-3 border ${
                !overAllocated && allocationValid
                  ? (overpayment > 0.01 ? 'bg-purple-50 border-purple-200' : 'bg-emerald-50 border-emerald-200')
                  : 'bg-red-50 border-red-200'
              }`}
              data-testid="allocation-summary"
            >
              <div className="flex justify-between items-center">
                <span className="text-sm font-semibold text-zinc-700">{t('allocated') || 'Allocated'}</span>
                <div className="text-right">
                  <span className={`font-mono font-bold text-lg ${!overAllocated && allocationValid ? 'text-emerald-700' : 'text-red-700'}`} data-testid="alloc-total">
                    &euro;{totalAllocated.toFixed(2)}
                  </span>
                  <span className="text-zinc-400 mx-1">/</span>
                  <span className="font-mono text-zinc-600">&euro;{totalAmount.toFixed(2)}</span>
                  {allocationValid && !overAllocated && overpayment < 0.01 && (
                    <CheckCircle size={16} weight="fill" className="inline ml-2 text-emerald-600" />
                  )}
                </div>
              </div>
              {overpayment > 0.01 && !overAllocated && (
                <div className="flex justify-between items-center text-sm border-t border-purple-200 pt-2" data-testid="overpayment-row">
                  <span className="text-purple-700 font-medium">{t('overpaymentCredit') || `${t('overpayment') || 'Overpayment'} → ${t('creditBalance') || 'Credit'}`}</span>
                  <span className="font-mono font-bold text-purple-700">&euro;{overpayment.toFixed(2)}</span>
                </div>
              )}
              {overAllocated && (
                <div className="text-xs text-red-700">
                  {t('allocationExceedsAmount') || 'Allocation exceeds the entered amount. Reduce some rows.'}
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          <div>
            <Label className="text-sm font-medium text-zinc-700">{t('notes')}</Label>
            <Input
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              className="mt-1 rounded-none"
              data-testid="payment-notes-input"
            />
          </div>

          <DialogFooter className="border-t border-zinc-200 pt-4 mt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)} className="rounded-none" data-testid="payment-cancel-btn">
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              className="bg-emerald-600 hover:bg-emerald-700 rounded-none px-6"
              disabled={!form.apartment_id || totalAmount <= 0 || overAllocated || saving}
              data-testid="payment-submit-btn"
            >
              {saving ? (t('saving') || 'Saving…') : t('recordPayment')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
